<script setup lang="ts">
// Fix relative URLs for GraphQL endpoints
const graphqlClients = useRuntimeConfig().public["graphql-client"]?.clients;
if (graphqlClients) {
	Object.entries(graphqlClients).forEach(([key, client]) => {
		const apiHost = typeof client === "string" ? client : client?.host;
		if (apiHost) {
			// check if the host is a relative URL and convert it to an absolute URL
			if (!/^https?:\/\//i.test(apiHost) && typeof window !== "undefined" && window.location) {
				const newHost = window.location.origin.replace(/\/$/, "") + (apiHost.startsWith("/") ? "" : "/") + apiHost;
				// If the host is relative, convert it to an absolute URL
				useGqlHost(newHost, key);
			}
		}
	});
}

const currentLocation = computed(() => encodeURIComponent(document.location.pathname + document.location.search));

const devProxyParam = computed(() => document.location.host.includes("localhost") ? `&devProxy=${encodeURIComponent(document.location.protocol + "//" + document.location.host)}` : "");

const connectivityStore = useConnectivityStore();
const appUserStore = useAppUserStore();

const checkAuthDialog = ref<boolean>(true);

const loginDialog = computed<boolean>(() => !checkAuthDialog.value && !appUserStore.wasAuthenticated && connectivityStore.needsAuthentication);
const errorDialog = computed<boolean>(() => !checkAuthDialog.value && !appUserStore.wasAuthenticated && appUserStore.hasError && !appUserStore.isError401);

// const showErrorSnackbar = computed<boolean>(() => appUserStore.wasAuthenticated && connectivityStore.hasErrors);
const userWasAuthenticated = computed<boolean>(() => appUserStore.wasAuthenticated);

const dialogTitle = computed<string>(() => {
	if (appUserStore.isError401) {
		return "Authentification nécessaire";
	}
	else if (appUserStore.isError403) {
		return "Problème lors de votre identification";
	}
	else {
		return "Oups !";
	}
});

// const isTimeout = ref(false); // Enable timeout with cookie expiration info
const isTimeout = ref(true); // no timeout for now
const countdownSeconds = ref(5 * 60); // 5 minutes en secondes

const popupContent = computed(() => {
	const minutes = Math.floor(countdownSeconds.value / 60);
	const seconds = countdownSeconds.value % 60;

	let timeMessage = "";
	if (minutes > 0 && seconds > 0) {
		timeMessage = `${minutes} minutes et ${seconds} secondes`;
	}
	else if (minutes > 0) {
		timeMessage = `${minutes} minutes`;
	}
	else {
		timeMessage = `${seconds} secondes`;
	}

	return isTimeout.value
		? {
				isTimeout: true,
				title: "Votre session a expiré",
				message: "Veuillez vous reconnecter afin d'accéder à MyIfen",
				imageSrc: "/ifen/my/img/pictos/divers/illu_timeout.svg",
				buttonText: "Se reconnecter",
			}
		: {
				isTimeout: false,
				title: "",
				message: `Votre session expire dans ${timeMessage}`,
				imageSrc: "/ifen/my/img/pictos/divers/sablier.svg",
				buttonText: "Rester connecté",
			};
});

// const showPopup = computed(() => isTimeout.value || connectivityStore.needsReconnect);
const showPopup = computed(() => appUserStore.wasAuthenticated && connectivityStore.needsAuthentication);

let countdownInterval: ReturnType<typeof setInterval> | null = null;

const startCountdown = () => {
	if (countdownInterval) clearInterval(countdownInterval);
	countdownInterval = setInterval(() => {
		if (countdownSeconds.value > 0) {
			countdownSeconds.value--;
		}
		else {
			if (countdownInterval !== null) {
				clearInterval(countdownInterval);
			}
			isTimeout.value = true;
		}
	}, 1000);
};

const stopCountdown = () => {
	if (countdownInterval) {
		clearInterval(countdownInterval);
		countdownInterval = null;
	}
};

onMounted(async () => {
	try {
		await appUserStore.getCurrentUser();
	}
	catch (e) {
		console.error(e);
	}
	finally {
		checkAuthDialog.value = false;
	}

	startCountdown();
});

onUnmounted(() => {
	stopCountdown();
});
</script>

<template>
	<!-- Popup for timeout session -->
	<div v-if="showPopup"
		class="popup-overlay">
		<div class="popup-content center">
			<img :src="popupContent.imageSrc"
				alt="timeout"
				class="mt-10"
				:class="{ 'sablier-animation': !popupContent.isTimeout }" />
			<h3 v-if="popupContent.title">
				{{ popupContent.title }}
			</h3>
			<h4 v-if="popupContent.message"
				class="mb-10">
				{{ popupContent.message }}
			</h4>
			<div class="popup-buttons mb-10 mt-10">
				<NuxtLink :href="`/ifen/my/api/login?returnUrl=${currentLocation}${devProxyParam}`"
					external
					class="action-link primary"
					role="button">
					<span>{{ popupContent.buttonText }}</span>
				</NuxtLink>
			</div>
		</div>
	</div>

	<NuxtLayout v-if="userWasAuthenticated">
		<NuxtPage />
	</NuxtLayout>

	<!-- If the user is not authenticated, display the login dialog -->
	<NuxtLayout v-else
		name="custom">
		<div v-if="checkAuthDialog"
			class="center">
			<p>Vérification de votre identité en cours...</p>
		</div>
		<div v-else-if="loginDialog || errorDialog"
			class="center">
			<h2>{{ dialogTitle }}</h2>
			<div v-if="loginDialog">
				<p>Vous devez vous connecter pour accéder votre espace MyIfen.</p>

				<div class="btn-group center">
					<NuxtLink :href="`/ifen/my/api/login?returnUrl=${currentLocation}${devProxyParam}`"
						external
						class="btn btn-secondary"
						role="button">
						<span>Se connecter</span>
					</NuxtLink>
				</div>
			</div>
			<div v-else-if="appUserStore.isError403"
				class="center">
				<div id="error_syclope"
					style="display: block; text-align: justify; padding: 5px !important;">
					<p style="font-size: 20px; font-weight: bold; color: red;">
						Problème lors de votre identification
					</p>
					<p>
						Si vous venez de vous connecter et d'activer votre compte IAM, merci de bien vouloir vous reconnecter dans 1h afin que vos données soient synchronisées.
						<br /><br />
						Si après ce délai, vous ne parvenez toujours pas à vous connecter, c’est que vous ne semblez pas être autorisé/e à créer un compte sur <a href="https://www.ifen.lu">www.ifen.lu</a>. Si vous pensez que ceci est une erreur de notre part, nous vous invitons à contacter l’IFEN à l’adresse <a href="MAILTO:foco@ifen.lu?subject=Controler la validation de l'acces a mon compte sur www.ifen.lu">foco@ifen.lu</a> en insérant comme objet à votre message "Contrôler la validation de l'accès à mon compte sur <a href="https://www.ifen.lu">www.ifen.lu</a>".
					</p>

					<hr style="margin-top: 19px; border-top: 1px dotted #3a3a3a" />

					<p style="font-size: 20px; font-weight: bold; color: red; margin-top: 20px;">
						Problem bei Ihrer Anmeldung
					</p>
					<p>
						Wenn Sie sich gerade angemeldet und Ihr IAM-Konto aktiviert haben, melden Sie sich bitte innerhalb 1 St. erneut an, damit Ihre Daten synchronisiert werden können.
						<br /><br />
						Wenn Sie sich nach dieser Frist immer noch nicht anmelden können, scheint es, dass Sie nicht berechtigt sind, ein Konto auf <a href="https://www.ifen.lu">www.ifen.lu</a> zu erstellen.
						<br /><br />
						Wenn Sie glauben, dass dies ein Fehler unsererseits ist, bitten wir Sie sich an IFEN zu wenden unter <a href="MAILTO:foco@ifen.lu?subject=Bitte uberprufen Sie den Zugang zu meinem Konto auf www.ifen.lu">foco@ifen.lu</a> mit folgendem Betreff Bitte überprüfen Sie den Zugang zu meinem Konto auf <a href="https://www.ifen.lu">www.ifen.lu</a>.
					</p>
				</div>
			</div>
			<div v-else
				class="center"
				style="min-height: 200px;">
				<div id="error_api"
					style="display: block; text-align: justify; padding: 5px !important;">
					<p style="font-size: 20px; font-weight: bold;">
						Un souci technique est survenu...
					</p>
					<p>
						Le service MyIfen n'est pas disponible pour le moment. Veuillez réessayer ultérieurement.
					</p>
				</div>
			</div>
		</div>
	</NuxtLayout>
</template>

<style scoped lang="scss">
@keyframes shake {
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(10deg);
  }
  50% {
    transform: rotate(0deg);
  }
  75% {
    transform: rotate(-10deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.sablier-animation {
  animation: shake 3s infinite;
}
</style>
